// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import { Modal, Backdrop, Paper, IconButton } from '@mui/material';
import { BiX } from 'react-icons/bi';
import { css } from '@emotion/css';
import MoveChatInterface from './MoveChatInterface';
import { markMessagesAsRead } from '@features/moves/services/smsReadStatusTracker';
import { useMoveChat } from '@features/moves/hooks/useMoveChat';
import { useValetSelfManaged } from '@hopdrive/feature-portal-products';

// TYPES ---------------------------------------------------------------- //

interface MoveChatModalProps {
  open: boolean;
  onClose: () => void;
  moveId?: number;
  consumerPickup?: boolean;
}

// COMPONENT ---------------------------------------------------------------- //

const MoveChatModal = ({ open, onClose, moveId, consumerPickup }: MoveChatModalProps) => {
  const cls = useStyles();
  
  // SMS chat is a self-managed Valet feature
  const isSmsEnabled = useValetSelfManaged();

  // Get messages to track for marking as read (always call hook, but conditionally enable)
  const { messages } = useMoveChat(moveId, consumerPickup, {
    enabled: isSmsEnabled && open && !!moveId && !!consumerPickup,
  });

  // Mark messages as read when modal opens and messages are loaded
  React.useEffect(() => {
    if (isSmsEnabled && open && moveId && messages && messages.length > 0) {
      // Mark as read after a short delay to ensure user actually views them
      const timer = setTimeout(() => {
        // Get the last message ID
        const sortedMessages = [...messages].sort((a, b) => {
          const dateA = new Date(a.createdat || 0).getTime();
          const dateB = new Date(b.createdat || 0).getTime();
          return dateB - dateA;
        });
        const lastMessage = sortedMessages[0];

        if (lastMessage?.id) {
          markMessagesAsRead(moveId, lastMessage.id);
        }
      }, 500); // 500ms delay to ensure user sees the messages

      return () => clearTimeout(timer);
    }
  }, [isSmsEnabled, open, moveId, messages]);

  // Don't render if not lite tier
  if (!isSmsEnabled) {
    return null;
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
        sx: { backgroundColor: 'transparent' },
      }}
    >
      <div className={cls.container}>
        <Paper
          elevation={8}
          sx={{
            width: 400,
            height: 600,
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            '@media (max-width: 480px)': { width: 'calc(100vw - 48px)', height: 500 },
          }}
        >
          <div className={cls.header}>
            <IconButton
              onClick={onClose}
              size='small'
              className={cls.closeButton}
              sx={{
                position: 'absolute',
                top: 10,
                right: 10,
                zIndex: 1001,
              }}
            >
              <BiX size={20} />
            </IconButton>
          </div>
          {open && moveId ? (
            <>
              <MoveChatInterface moveId={moveId} consumerPickup={consumerPickup} />
            </>
          ) : null}
        </Paper>
      </div>
    </Modal>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = () => ({
  container: css`
    position: fixed;
    bottom: 24px;
    right: 24px;
    z-index: 1300;
    outline: none;
  `,

  header: css`
    position: relative;
    height: 0;
  `,

  closeButton: css`
    background-color: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(4px);

    &:hover {
      background-color: rgba(255, 255, 255, 1);
    }
  `,
});

// EXPORT ---------------------------------------------------------------- //

export default MoveChatModal;
